import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  CircleGeometry,
  Color,
  Mesh,
  MeshBasicMaterial,
  NormalBlending,
  Points,
  Quaternion,
  ShaderMaterial,
  Vector3,
} from 'three';
import type { Blending, Scene } from 'three';
import { softDotTexture } from './textures.js';

const VERTEX = /* glsl */ `
  attribute float size;
  attribute vec4 tint;
  varying vec4 vTint;
  void main() {
    vTint = tint;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (420.0 / max(0.5, -mv.z));
    gl_Position = projectionMatrix * mv;
  }
`;

const FRAGMENT = /* glsl */ `
  varying vec4 vTint;
  void main() {
    float d = length(gl_PointCoord - 0.5);
    float a = vTint.a * (1.0 - smoothstep(0.15, 0.5, d));
    if (a < 0.01) discard;
    gl_FragColor = vec4(vTint.rgb, a);
  }
`;

interface Particle {
  life: number;
  maxLife: number;
  position: Vector3;
  velocity: Vector3;
  gravity: number;
  drag: number;
  size: number;
  grow: number;
  color: Color;
  alpha: number;
}

class ParticlePool {
  public readonly points: Points;
  private readonly particles: Particle[] = [];
  private readonly positions: Float32Array;
  private readonly sizes: Float32Array;
  private readonly tints: Float32Array;
  private cursor = 0;

  public constructor(capacity: number, blending: Blending) {
    this.positions = new Float32Array(capacity * 3);
    this.sizes = new Float32Array(capacity);
    this.tints = new Float32Array(capacity * 4);
    for (let i = 0; i < capacity; i += 1) {
      this.particles.push({
        life: 0,
        maxLife: 1,
        position: new Vector3(),
        velocity: new Vector3(),
        gravity: 0,
        drag: 0,
        size: 0,
        grow: 0,
        color: new Color(),
        alpha: 0,
      });
    }
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    geometry.setAttribute('size', new BufferAttribute(this.sizes, 1));
    geometry.setAttribute('tint', new BufferAttribute(this.tints, 4));
    const material = new ShaderMaterial({
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthWrite: false,
      blending,
    });
    this.points = new Points(geometry, material);
    this.points.frustumCulled = false;
    this.points.renderOrder = 3;
  }

  public emit(options: {
    position: Vector3;
    velocity: Vector3;
    life: number;
    size: number;
    color: Color;
    gravity?: number;
    drag?: number;
    grow?: number;
    alpha?: number;
  }): void {
    const p = this.particles[this.cursor]!;
    this.cursor = (this.cursor + 1) % this.particles.length;
    p.life = options.life;
    p.maxLife = options.life;
    p.position.copy(options.position);
    p.velocity.copy(options.velocity);
    p.gravity = options.gravity ?? 0;
    p.drag = options.drag ?? 0;
    p.size = options.size;
    p.grow = options.grow ?? 0;
    p.color.copy(options.color);
    p.alpha = options.alpha ?? 1;
  }

  public update(dt: number): void {
    this.particles.forEach((p, i) => {
      if (p.life > 0) {
        p.life -= dt;
        p.velocity.y -= p.gravity * dt;
        p.velocity.multiplyScalar(Math.exp(-p.drag * dt));
        p.position.addScaledVector(p.velocity, dt);
        p.size += p.grow * dt;
      }
      const alive = p.life > 0;
      const t = alive ? p.life / p.maxLife : 0;
      this.positions[i * 3] = p.position.x;
      this.positions[i * 3 + 1] = p.position.y;
      this.positions[i * 3 + 2] = p.position.z;
      this.sizes[i] = alive ? p.size : 0;
      this.tints[i * 4] = p.color.r;
      this.tints[i * 4 + 1] = p.color.g;
      this.tints[i * 4 + 2] = p.color.b;
      this.tints[i * 4 + 3] = alive ? p.alpha * Math.min(1, t * 2.5) : 0;
    });
    const geometry = this.points.geometry;
    geometry.getAttribute('position').needsUpdate = true;
    geometry.getAttribute('size').needsUpdate = true;
    geometry.getAttribute('tint').needsUpdate = true;
  }
}

const SPARK = new Color(4, 2.2, 0.6);
const DUST = new Color('#b9a58e');
const DIRT = new Color('#8a6a48');
const FLASH = new Color(6, 5, 3);

/** Grind sparks, landing dust, hit bursts and rider blob shadows. */
export class Effects {
  private readonly additive = new ParticlePool(700, AdditiveBlending);
  private readonly soft = new ParticlePool(400, NormalBlending);
  private readonly shadowMaterial: MeshBasicMaterial;
  private readonly shadowGeometry = new CircleGeometry(0.55, 20);
  private readonly shadows = new Map<string, Mesh>();

  public constructor(private readonly scene: Scene) {
    scene.add(this.additive.points, this.soft.points);
    this.shadowMaterial = new MeshBasicMaterial({
      map: softDotTexture(),
      color: new Color('#000000'),
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -4,
    });
  }

  public sparks(position: Vector3, direction: Vector3, intensity = 1): void {
    const count = Math.round(3 * intensity);
    for (let i = 0; i < count; i += 1) {
      this.additive.emit({
        position,
        velocity: new Vector3(
          -direction.x * (2 + Math.random() * 4) + (Math.random() - 0.5) * 3,
          1 + Math.random() * 3,
          -direction.z * (2 + Math.random() * 4) + (Math.random() - 0.5) * 3,
        ),
        life: 0.25 + Math.random() * 0.35,
        size: 0.06 + Math.random() * 0.05,
        color: SPARK,
        gravity: 14,
      });
    }
  }

  public dust(position: Vector3, amount: number, dirt = false): void {
    const count = Math.round(6 + amount * 10);
    for (let i = 0; i < count; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1 + Math.random() * 2.5 * (0.5 + amount);
      this.soft.emit({
        position: new Vector3(position.x, position.y + 0.1, position.z),
        velocity: new Vector3(
          Math.cos(angle) * speed,
          0.4 + Math.random() * 1.2,
          Math.sin(angle) * speed,
        ),
        life: 0.6 + Math.random() * 0.6,
        size: 0.35 + Math.random() * 0.3,
        grow: 1.4,
        drag: 3,
        color: dirt ? DIRT : DUST,
        alpha: 0.45,
      });
    }
  }

  public hit(position: Vector3, heavy: boolean): void {
    this.additive.emit({
      position,
      velocity: new Vector3(),
      life: 0.16,
      size: heavy ? 2.4 : 1.6,
      color: FLASH,
      grow: 6,
    });
    const count = heavy ? 22 : 14;
    for (let i = 0; i < count; i += 1) {
      const v = new Vector3(Math.random() - 0.5, Math.random() - 0.3, Math.random() - 0.5)
        .normalize()
        .multiplyScalar(4 + Math.random() * 6);
      this.additive.emit({
        position,
        velocity: v,
        life: 0.2 + Math.random() * 0.25,
        size: 0.08 + Math.random() * 0.08,
        color: i % 3 === 0 ? FLASH : SPARK,
        gravity: 6,
        drag: 2,
      });
    }
  }

  /** Places a soft blob shadow for a rider on the surface below. */
  public shadow(
    id: string,
    visible: boolean,
    point: Vector3 | null,
    normal: Vector3 | null,
    height: number,
  ): void {
    let mesh = this.shadows.get(id);
    if (!mesh) {
      mesh = new Mesh(this.shadowGeometry, this.shadowMaterial.clone());
      mesh.renderOrder = 1;
      this.scene.add(mesh);
      this.shadows.set(id, mesh);
    }
    mesh.visible = visible && point !== null && normal !== null;
    if (!mesh.visible || !point || !normal) return;
    mesh.position.copy(point).addScaledVector(normal, 0.03);
    mesh.quaternion.copy(new Quaternion().setFromUnitVectors(new Vector3(0, 0, 1), normal));
    const fade = Math.max(0, 1 - height / 8);
    (mesh.material as MeshBasicMaterial).opacity = 0.5 * fade;
    mesh.scale.setScalar(1 + height * 0.12);
  }

  public removeShadow(id: string): void {
    const mesh = this.shadows.get(id);
    if (mesh) {
      this.scene.remove(mesh);
      (mesh.material as MeshBasicMaterial).dispose();
      this.shadows.delete(id);
    }
  }

  public update(dt: number): void {
    this.additive.update(dt);
    this.soft.update(dt);
  }

  public dispose(): void {
    this.scene.remove(this.additive.points, this.soft.points);
    for (const id of [...this.shadows.keys()]) this.removeShadow(id);
  }
}
